import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useUserContext } from "../Context/UserContext";

/** Recover request text in this tab, scoped to the signed-in customer and workspace. */
export function useRequestDraft(
  questionId: string,
  latestReviewId: string | null,
  ready: boolean
) {
  const { user } = useUserContext();
  const storageKey =
    user?.id && user.active_org_id
      ? `reviewed.request-draft.${user.id}.${user.active_org_id}.${questionId}`
      : null;
  const [body, setBody] = useState("");
  const [parties, setParties] = useState("");
  const [saved, setSaved] = useState(true);
  const submissionKey = useRef("");
  const exists = useRef(false);
  useLayoutEffect(() => {
    setBody("");
    setParties("");
    exists.current = false;
    submissionKey.current = crypto.randomUUID();
    setSaved(Boolean(storageKey));
    if (!storageKey || !ready) return;
    try {
      const entry = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (
        entry?.baseReviewId === latestReviewId &&
        typeof entry?.body === "string" &&
        typeof entry?.submissionKey === "string"
      ) {
        setBody(entry.body.slice(0, 4000));
        setParties(
          typeof entry.parties === "string" ? entry.parties.slice(0, 2000) : ""
        );
        submissionKey.current = entry.submissionKey;
        exists.current = true;
      } else if (entry) sessionStorage.removeItem(storageKey);
    } catch {
      setSaved(false);
    }
  }, [storageKey, latestReviewId, ready]);
  const change = useCallback(
    (value: string, newKey = true, partyValue = parties) => {
      setBody(value);
      exists.current = true;
      if (newKey) submissionKey.current = crypto.randomUUID();
      try {
        if (!storageKey) throw new Error("Draft storage unavailable");
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            body: value,
            parties: partyValue,
            submissionKey: submissionKey.current,
            baseReviewId: latestReviewId,
          })
        );
        setSaved(true);
      } catch {
        setSaved(false);
      }
    },
    [storageKey, latestReviewId, parties]
  );
  const initialize = (fallback: string) => {
    if (!exists.current) change(fallback.slice(0, 4000));
  };
  const clear = () => {
    try {
      if (storageKey) sessionStorage.removeItem(storageKey);
    } catch {
      /* no persistent entry when storage fails */
    }
    exists.current = false;
    setBody("");
    setParties("");
    submissionKey.current = crypto.randomUUID();
  };
  useEffect(() => {
    if (!body || saved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const guard = (event: MouseEvent) => {
      if (
        !(event.target instanceof Element) ||
        !event.target.closest("a[href]")
      )
        return;
      if (
        !window.confirm(
          "Your request draft could not be saved. Leave and discard it?"
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("click", guard, true);
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.removeEventListener("click", guard, true);
    };
  }, [body, saved]);
  return {
    body,
    parties,
    changeParties: (value: string) => {
      setParties(value);
      change(body, true, value);
    },
    change,
    initialize,
    clear,
    submissionKey,
    saved,
    ready: Boolean(storageKey && ready),
  };
}
